import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialAccountsStore, $initialCardsStore } from '@lib/store';
import { AccountType, CardKind } from '@shared/api';

interface UseTabsProps {
  withDebitCard?: boolean;
}

export const useTabs = (props: UseTabsProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsStore = useUnit($initialCardsStore.store);

  const [tab, setTab] = useState<AccountType | null>(null);

  const { t } = useTranslation();

  const loading = useMemo(() => accountsStore.loading || cardsStore.loading, [accountsStore.loading, cardsStore.loading]);
  const hasPayment = useMemo(() => accountsStore.data.some((account) => account.type === AccountType.Payment), [accountsStore.data]);
  const hasCreditCard = useMemo(() => cardsStore.data.some((card) => card.cardKind === CardKind.Credit), [cardsStore.data]);
  const hasDebitCard = useMemo(() => cardsStore.data.some((card) => card.cardKind === CardKind.Debit), [cardsStore.data]);
  const hasCard = useMemo(() => hasCreditCard || hasDebitCard, [hasCreditCard, hasDebitCard]);

  useEffect(() => {
    if (loading) return;
    const tab = location.hash.replace('#', '');
    if (hasPayment && (hasCreditCard || (hasDebitCard && props.withDebitCard)) && (tab === AccountType.Payment || tab === AccountType.Card))
      setTab(tab);
    else if (hasPayment && (hasCreditCard || (hasDebitCard && props.withDebitCard))) setTab(AccountType.Payment);
    else if (hasPayment) setTab(AccountType.Payment);
    else if (hasCreditCard) setTab(AccountType.Card);
    else if (hasDebitCard && props.withDebitCard) setTab(AccountType.Card);
    else setTab(null);
  }, [loading, hasPayment, hasCreditCard, hasDebitCard, props.withDebitCard]);

  const changeTabHandler = useCallback((tab: AccountType) => {
    setTab(tab);
    navigate(`#${tab}`, { replace: true });
  }, []);

  const tabs = useMemo(
    () => [
      { label: t('accountType.payments'), value: AccountType.Payment },
      { label: t('accountType.cards'), value: AccountType.Card },
    ],
    [t],
  );

  return useMemo(
    () => ({
      loading,
      hasCreditCard,
      hasDebitCard,
      hasCard,
      hasPayment,
      tabs,
      tab,
      onChangeTab: changeTabHandler,
    }),
    [loading, hasCreditCard, hasDebitCard, hasCard, hasPayment, tabs, tab, changeTabHandler],
  );
};
